import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MuiCard } from "@ace/ace-ui-web";
import { CircularProgress, Typography, Stack, Grid } from '@mui/material';
import { CalendarMonth, Cancel, Layers, Percent, SignalCellularAlt, Timer } from "@mui/icons-material";
import { toast } from "sonner";
import StatCard from "../../components/statcard";
import styles from "./gigReport.module.css";
import { getDefaultReportStartDate, getDefaultReportEndDate } from '../../utils/reportDateDefaults';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchGigReport } from '../../redux/GigReportReducer/gigReportSlice';

const calculatePercentage = (value: number, total: number): number => {
    if (!total) return 0;
    return +(value / total * 100).toFixed(1);
};

const GigReportPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { gigReportData, loading, error } = useAppSelector((state) => state.gigReport);

    const [filter, setFilter] = useState({
        fromDate: getDefaultReportStartDate(),
        toDate: getDefaultReportEndDate(),
    });

    const dispatchFetchGigReport = useCallback((fromDate: string, toDate: string) => {
        if (!currentUser?.siteCd) return;
        dispatch(fetchGigReport({
            siteCd: currentUser.siteCd,
            startDate: fromDate || undefined,
            endDate: toDate || undefined,
        }));
    }, [dispatch, currentUser?.siteCd]);

    useEffect(() => {
        dispatchFetchGigReport(filter.fromDate, filter.toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchFetchGigReport]);

    useEffect(() => {
        if (error) {
            toast.error(`Failed to load gig report data: ${error}`);
        }
    }, [error]);

    const statistics = useMemo(() => {
        const totalGigShifts = gigReportData?.totalRows ?? 0;
        const approvedGigShifts = gigReportData?.approvedRows ?? 0;
        const rejectedGigShifts = gigReportData?.rejectedRows ?? 0;
        const totalGigHours = gigReportData?.totalGigHours ?? 0;
        const sftGigShifts = gigReportData?.gigShiftPickup ?? 0;
        const swapGigShifts = gigReportData?.gigSwapPickup ?? 0;

        return {
            totalGigShifts,
            approvedGigShifts,
            rejectedGigShifts,
            totalGigHours,
            approvedGigShiftPercentage: calculatePercentage(approvedGigShifts, totalGigShifts),
            requestGigShifts: totalGigShifts,
            sftGigShifts,
            swapGigShifts,
        };
    }, [gigReportData]);

    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setFilter(prev => ({ ...prev, fromDate: newDate }));
        dispatchFetchGigReport(newDate, filter.toDate);
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setFilter(prev => ({ ...prev, toDate: newDate }));
        dispatchFetchGigReport(filter.fromDate, newDate);
    };

    return (
        <Stack spacing={2.5} sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
            {/* Filter Card */}
            <MuiCard
                borderRadius={2}
                elevation={5}
                padding={0}
                variant="elevation"
                sx={{ width: '100%' }}
            >
                <Stack spacing={2} sx={{ p: 2 }}>
                    <Typography variant="h6">
                        Date Range Filter
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={0.5} className={[styles.formGroup, 'form-group'].join(" ")}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CalendarMonth sx={{ fontSize: 15, color: "gray" }} />
                                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center' }}>Start Date</Typography>
                                </Stack>
                                <input
                                    id="gigFromDate"
                                    type="date"
                                    value={filter.fromDate}
                                    max={filter.toDate}
                                    onChange={handleFromDateChange}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={0.5} className={[styles.formGroup, 'form-group'].join(" ")}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CalendarMonth sx={{ fontSize: 15, color: "gray" }} />
                                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center' }}>End Date</Typography>
                                </Stack>
                                <input
                                    id="gigToDate"
                                    type="date"
                                    value={filter.toDate}
                                    min={filter.fromDate}
                                    onChange={handleToDateChange}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </MuiCard>

            {loading ? (
                <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '300px' }}>
                    <CircularProgress />
                    <Typography variant="body1">Loading gig report data...</Typography>
                </Stack>
            ) : (
                <>
                    {/* Gig Shift Summary */}
                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ pt: 1.5 }}>
                            Gig Shift Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                                <StatCard
                                    value={statistics.totalGigShifts}
                                    title="Total Gig Shifts"
                                    note="All gig requests within date range"
                                    IconProp={<Layers />}
                                    colorProp="lightblue"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                                <StatCard
                                    value={statistics.approvedGigShiftPercentage}
                                    isPercentage={true}
                                    title="Fill Rate"
                                    note={`${statistics.approvedGigShifts} approved of ${statistics.totalGigShifts} requested shifts`}
                                    IconProp={<Percent />}
                                    colorProp="green"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                                <StatCard
                                    value={statistics.rejectedGigShifts}
                                    title="Rejected Gig Shifts"
                                    note="Gig work requests that were rejected"
                                    IconProp={<Cancel />}
                                    colorProp="red"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                                <StatCard
                                    value={statistics.totalGigHours}
                                    title="Total Gig Hours"
                                    note="Total requested hours"
                                    IconProp={<Timer />}
                                    colorProp="yellow"
                                />
                            </Grid>
                        </Grid>
                    </Stack>

                    {/* Gig Type Breakdown */}
                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ pt: 1.5 }}>
                                Gig Type Breakdown
                                <Typography variant="subtitle2" sx={{ color:"#888888" }}>(Approved Shifts Only)</Typography>
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.approvedGigShifts}
                                    title="Request"
                                    note="Shift requests submitted by gig workers"
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="blue"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.approvedGigShifts, statistics.approvedGigShifts + statistics.sftGigShifts + statistics.swapGigShifts)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.sftGigShifts}
                                    title="Shift"
                                    note="Supervisor-posted shifts picked up by gig workers"
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="black"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.sftGigShifts, statistics.approvedGigShifts + statistics.sftGigShifts + statistics.swapGigShifts)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.swapGigShifts}
                                    title="Swap"
                                    note="Swap shifts picked up by gig workers"
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="yellow"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.swapGigShifts, statistics.approvedGigShifts + statistics.sftGigShifts + statistics.swapGigShifts)}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default GigReportPage;
